import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { normalizePartnerName, DUPLICATE_NAME_ERROR } from "@/lib/normalizeName";

export function useClientes() {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["clientes", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clientes")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });
}

export function useCreateCliente() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { nombre: string; nit?: string; pais?: string }) => {
      if (!orgId) throw new Error("Sin organización");

      // ── Capa 2: pre-check contra cache local ──────────────────────────
      const normalized = normalizePartnerName(values.nombre);
      if (!normalized) throw new Error("El nombre es obligatorio");

      const cached = queryClient.getQueryData<any[]>(["clientes", orgId]) ?? [];
      const localDup = cached.find(
        (c) => normalizePartnerName(c?.nombre) === normalized,
      );
      if (localDup) {
        const err: any = new Error(DUPLICATE_NAME_ERROR);
        err.code = "DUPLICATE_NAME";
        err.existing = localDup;
        throw err;
      }

      // ── Insert defensivo ─────────────────────────────────────────────
      const { data, error } = await supabase
        .from("clientes")
        .insert({
          org_id: orgId,
          nombre: values.nombre.trim(),
          nit: values.nit?.trim() || null,
          pais: values.pais?.trim() || null,
        })
        .select()
        .single();

      if (error) {
        if ((error as any).code === "23505") {
          const err: any = new Error(DUPLICATE_NAME_ERROR);
          err.code = "DUPLICATE_NAME";
          throw err;
        }
        throw error;
      }
      return data as { id: string; nombre: string; nit: string | null };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientes", orgId] });
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
    },
  });
}

export function useDeleteCliente() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("clientes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientes", orgId] });
    },
  });
}
